import { useEffect, useState } from "react";
import { Users, UserCheck, Dumbbell, Calendar, DollarSign, CreditCard, AlertCircle, Clock, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getUser } from "@/utils/auth";
import { useSettings } from "@/context/SettingsContext";
import api from "@/utils/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const { formatCurrency, formatDate, settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [expiringMemberships, setExpiringMemberships] = useState([]);
  const [pendingAdmissions, setPendingAdmissions] = useState([]);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [settings.membershipDueReminder]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, membershipsRes, membersRes, attendanceRes, financialRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/memberships'),
        api.get('/members'),
        api.get('/attendance'),
        api.get('/financial-summary'),
      ]);

      setStats(statsRes.data);
      setFinancialSummary(financialRes.data);

      // Filter expiring memberships
      const reminderDays = settings.membershipDueReminder || 7;
      const today = new Date();
      const reminderDate = new Date(today);
      reminderDate.setDate(today.getDate() + reminderDays);

      const expiring = membershipsRes.data.filter(m => {
        const endDate = new Date(m.end_date);
        return m.status === 'active' && endDate >= today && endDate <= reminderDate;
      });

      // Get member details for expiring memberships
      const expiringWithDetails = await Promise.all(
        expiring.map(async (membership) => {
          const member = membersRes.data.find(m => m.id === membership.member_id);
          return { ...membership, memberName: member?.name || 'Unknown' };
        })
      );
      setExpiringMemberships(expiringWithDetails);

      // Filter pending admissions
      const pending = membersRes.data.filter(m => 
        m.admission_fee_amount && !m.admission_fee_paid
      );
      setPendingAdmissions(pending);

      // Recent attendance (last 10)
      const recent = attendanceRes.data.slice(0, 10).map(a => {
        const member = membersRes.data.find(m => m.id === a.member_id);
        return { ...a, memberName: member?.name || 'Unknown' };
      });
      setRecentAttendance(recent);

    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  const statCards = stats ? [
    {
      icon: Users,
      label: "Total Members",
      value: stats.total_members,
      color: "bg-blue-500",
      link: "/members"
    },
    {
      icon: UserCheck,
      label: "Active Members",
      value: stats.active_members,
      color: "bg-lime-500",
      link: "/members"
    },
    {
      icon: Dumbbell,
      label: "Trainers",
      value: stats.total_trainers,
      color: "bg-orange-500",
      link: "/trainers"
    },
    {
      icon: Calendar,
      label: "Today's Attendance",
      value: stats.today_attendance,
      color: "bg-purple-500",
      link: "/attendance"
    },
    {
      icon: DollarSign,
      label: "Revenue",
      value: formatCurrency(financialSummary?.net_profit || 0),
      color: "bg-green-500",
      link: "/financial-ledger"
    },
    {
      icon: CreditCard,
      label: "Active Memberships",
      value: stats.active_memberships,
      color: "bg-pink-500",
      link: "/subscriptions"
    },
  ] : [];

  const alertCards = stats ? [
    {
      icon: AlertCircle,
      label: "Expiring Soon",
      value: stats.expiring_soon,
      color: "bg-orange-50 border-orange-200",
      textColor: "text-orange-700",
      iconColor: "text-orange-500",
      action: () => {},
    },
    {
      icon: DollarSign,
      label: "Pending Admissions",
      value: stats.pending_admission_fees,
      color: "bg-yellow-50 border-yellow-200",
      textColor: "text-yellow-700",
      iconColor: "text-yellow-500",
      action: () => navigate("/members"),
    },
    {
      icon: Clock,
      label: "Trainer Payments Due",
      value: formatCurrency(stats.trainer_pending_payments),
      color: "bg-red-50 border-red-200",
      textColor: "text-red-700",
      iconColor: "text-red-500",
      action: () => navigate("/trainers"),
    },
    {
      icon: CreditCard,
      label: "Pending Subscription Fees",
      value: formatCurrency(stats.pending_subscription_fees || 0),
      color: "bg-purple-50 border-purple-200",
      textColor: "text-purple-700",
      iconColor: "text-purple-500",
      action: () => navigate("/subscriptions"),
    },
  ] : [];

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-zinc-500">
          {(() => {
            const user = getUser();
            const companyName = user?.company?.name || user?.tenant?.name;
            return companyName ? `Overview of your ${companyName}` : 'Overview of your gym management';
          })()}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${stat.label.toLowerCase().replace(/\\s+/g, '-')}`}
              className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all group cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-zinc-500 uppercase tracking-wider mb-2">
                    {stat.label}
                  </p>
                  <p className="text-3xl font-bold text-zinc-900">
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {alertCards.map((alert, index) => {
          const Icon = alert.icon;
          return (
            <div
              key={index}
              onClick={alert.action}
              className={`${alert.color} border-2 rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className={alert.iconColor} size={28} />
                <span className={`text-3xl font-black ${alert.textColor}`}>
                  {alert.value}
                </span>
              </div>
              <p className={`text-sm font-bold ${alert.textColor} uppercase tracking-wider`}>
                {alert.label}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expiring Memberships */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200 bg-orange-50">
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <AlertCircle className="text-orange-600" size={20} />
              Expiring Soon
            </h2>
          </div>
          <div className="overflow-y-auto max-h-80">
            {expiringMemberships.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No memberships expiring in the next 7 days
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {expiringMemberships.map((membership) => {
                  const daysLeft = Math.ceil((new Date(membership.end_date) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={membership.id} className="px-6 py-4 hover:bg-orange-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium text-zinc-900">{membership.memberName}</h3>
                          <p className="text-xs text-zinc-500 mt-1">
                            Expires: {formatDate(membership.end_date)}
                          </p>
                        </div>
                        <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">
                          {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Pending Admissions */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-zinc-200 bg-yellow-50">
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <DollarSign className="text-yellow-600" size={20} />
              Pending Admission Fees
            </h2>
          </div>
          <div className="overflow-y-auto max-h-80">
            {pendingAdmissions.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No pending admission fees
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {pendingAdmissions.map((member) => (
                  <div key={member.id} className="px-6 py-4 hover:bg-yellow-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-zinc-900">{member.name}</h3>
                        <p className="text-xs text-zinc-500 mt-1">{member.email}</p>
                      </div>
                      <span className="text-sm font-bold text-yellow-700">
                        {formatCurrency(member.admission_fee_amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Recent Attendance */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm lg:col-span-2">
          <div className="px-6 py-4 border-b border-zinc-200 bg-blue-50">
            <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <TrendingUp className="text-blue-600" size={20} />
              Recent Activity
            </h2>
          </div>
          <div className="overflow-y-auto max-h-80">
            {recentAttendance.length === 0 ? (
              <p className="px-6 py-12 text-center text-zinc-500">
                No recent attendance
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {recentAttendance.map((record) => (
                  <div key={record.id} className="px-6 py-4 hover:bg-blue-50 transition-colors">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-medium text-zinc-900">{record.memberName}</h3>
                        <p className="text-xs text-zinc-500 mt-1">
                          {formatDate(record.date)} • {new Date(record.check_in_time).toLocaleTimeString()}
                        </p>
                      </div>
                      <Calendar className="text-blue-500" size={20} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
